import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Lock } from "lucide-react";

/**
 * The shared wizard used for both the Starting and Finishing assessments.
 * - 20 Likert statements, grouped into 4 movement screens
 * - 1 reflections screen (3 prompts for start, 6 for finish)
 * - 1 review/submit screen
 * - Save-and-finish-later: debounced draft saves after any change
 *
 * Props:
 *  - type: "start" | "finish"
 */
export default function AssessmentWizard({ type }) {
  const nav = useNavigate();
  const isFinish = type === "finish";
  const [content, setContent] = useState(null);
  const [answers, setAnswers] = useState(() => Array(20).fill(0));
  const [reflections, setReflections] = useState({});
  const [startRef, setStartRef] = useState(null); // for finish: show user's week-1 r1-r3
  const [screen, setScreen] = useState(0); // 0..3 movements, 4 reflections, 5 review
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dirty = useRef(false);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [screen]);

  // Load content, draft, and prior start reflections (if finish)
  useEffect(() => {
    (async () => {
      try {
        const [c, cur, startDoc, status] = await Promise.all([
          api.get("/assessment/content"),
          api.get(`/assessment/${type}`),
          isFinish ? api.get("/assessment/start").catch(() => ({ data: null })) : Promise.resolve(null),
          isFinish ? api.get("/assessment/status") : Promise.resolve(null),
        ]);
        if (isFinish && !status.data.finish_unlocked && !cur.data?.locked) {
          toast.info("The Finishing Assessment opens after you complete Week 10.", { id: "finish-locked" });
          nav("/app", { replace: true });
          return;
        }
        setContent(c.data);
        if (cur.data?.locked) {
          // Already submitted — jump out
          toast.info("You've already submitted this assessment.");
          nav(isFinish ? "/app/assessment/compare" : "/app/assessment/start/results", { replace: true });
          return;
        }
        if (cur.data?.exists) {
          const a = cur.data.answers || [];
          setAnswers(Array.from({ length: 20 }, (_, i) => a[i] || 0));
          setReflections(cur.data.reflections || {});
        }
        if (startDoc?.data?.locked) {
          setStartRef(startDoc.data.reflections || {});
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load the assessment.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Debounced draft save on changes
  useEffect(() => {
    if (loading) return;
    if (!dirty.current) { dirty.current = true; return; }
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        await api.post(`/assessment/${type}/draft`, { answers, reflections });
      } catch { /* silent — draft is best-effort */ }
      finally { setSaving(false); }
    }, 600);
    return () => clearTimeout(t);
  }, [answers, reflections, loading, type]);

  const needKeys = isFinish ? ["r1","r2","r3","r4","r5","r6"] : ["r1","r2","r3"];
  const totalScreens = 6; // 4 movements + 1 reflections + 1 review

  const allRated = useMemo(() => answers.every((v) => v >= 1 && v <= 5), [answers]);
  const allReflected = useMemo(
    () => needKeys.every((k) => (reflections[k] || "").trim().length >= 1),
    [reflections, needKeys],
  );

  const submit = async () => {
    if (!allRated) { toast.error("A couple of statements still need a rating."); return; }
    if (!allReflected) { toast.error("Please answer each reflection question."); return; }
    setSubmitting(true);
    try {
      await api.post(`/assessment/${type}/submit`, { answers, reflections });
      toast.success("Assessment submitted.");
      nav(isFinish ? "/app/assessment/compare" : "/app/assessment/start/results", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !content) {
    return <div className="py-20 text-center text-muted-foreground">Preparing…</div>;
  }

  const mv = screen < 4 ? content.movements[screen] : null;
  const reflectionPrompts = isFinish ? content.reflections_finish : content.reflections_start;
  const screenLabel = screen < 4
    ? `${mv.title} · ${screen + 1} of ${totalScreens}`
    : screen === 4 ? `Reflections · 5 of ${totalScreens}` : `Review · ${totalScreens} of ${totalScreens}`;

  const canNext = screen < 4
    ? mv.statements.every((s) => answers[s.index - 1] >= 1)
    : screen === 4
      ? needKeys.every((k) => (reflections[k] || "").trim().length >= 1)
      : true;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary">
            {isFinish ? "Finishing Assessment · Week 11" : "Starting Assessment · Week 1"}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-2 leading-tight">
            {isFinish ? "Look back. Look up." : "A mirror, and a gentle one."}
          </h1>
        </div>
        <div className="text-xs text-muted-foreground tabular-nums">
          {saving ? <span className="italic">Saving…</span> : <span>Draft saved</span>}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-secondary/60 overflow-hidden mb-10">
        <div className="h-full bg-primary transition-all" style={{ width: `${((screen + 1) / totalScreens) * 100}%` }} />
      </div>

      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
        {screenLabel}
      </div>

      {/* Opening words only on first screen of starting assessment */}
      {screen === 0 && !isFinish && (
        <p data-testid="assessment-opening" className="font-serif italic text-lg leading-relaxed mb-10 border-l-2 border-primary pl-5">
          {content.opening_words}
        </p>
      )}

      {/* Finish: show prior week-1 r1-r3 above the first screen */}
      {screen === 0 && isFinish && startRef && (
        <section data-testid="assessment-prior-reflections" className="mb-10 border border-border bg-card p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-accent/80 mb-3">Your words eleven weeks ago</div>
          {content.reflections_start.map((p) => (
            <div key={p.key} className="mb-4 last:mb-0">
              <div className="text-sm text-muted-foreground mb-1">{p.prompt}</div>
              <div className="font-serif italic leading-relaxed">
                {(startRef[p.key] || "—").trim()}
              </div>
            </div>
          ))}
        </section>
      )}

      {screen < 4 && (
        <section>
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">{mv.scripture_ref}</div>
            <blockquote className="font-serif italic text-lg leading-relaxed border-l-2 border-primary/40 pl-5">
              "{mv.scripture_text}"
            </blockquote>
          </div>
          <ul className="space-y-10">
            {mv.statements.map((s) => (
              <li key={s.index} data-testid={`assessment-statement-${s.index}`}>
                <p className="font-serif text-lg leading-relaxed mb-4">
                  <span className="text-muted-foreground text-sm tabular-nums mr-2">{s.index}.</span>
                  {s.text}
                </p>
                <div className="grid grid-cols-5 gap-2">
                  {content.scale.map((opt) => {
                    const picked = answers[s.index - 1] === opt.value;
                    return (
                      <button key={opt.value}
                        type="button"
                        data-testid={`assessment-rate-${s.index}-${opt.value}`}
                        onClick={() => setAnswers((a) => a.map((v, i) => i === s.index - 1 ? opt.value : v))}
                        className={`border px-3 py-3 text-center transition-colors ${
                          picked
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border hover:border-primary/50 hover:bg-secondary/40"
                        }`}>
                        <div className="font-serif text-2xl">{opt.value}</div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1 leading-tight">
                          {opt.label}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {screen === 4 && (
        <section>
          <p className="text-muted-foreground mb-8 max-w-2xl">
            Short answers are welcome. Honest answers are better than polished ones.
          </p>
          <div className="space-y-10">
            {reflectionPrompts.map((p) => (
              <div key={p.key} data-testid={`assessment-reflection-${p.key}`}>
                <label className="font-serif italic text-lg leading-relaxed block mb-3">
                  {p.prompt}
                </label>
                <Textarea
                  data-testid={`assessment-reflection-input-${p.key}`}
                  rows={4} value={reflections[p.key] || ""}
                  onChange={(e) => setReflections((r) => ({ ...r, [p.key]: e.target.value }))}
                  placeholder="Write freely — nobody but you will see this unless you choose to share."
                  className="bg-transparent border-0 border-b rounded-none focus-visible:ring-0 focus-visible:border-primary leading-relaxed"
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {screen === 5 && (
        <section data-testid="assessment-review">
          <p className="text-muted-foreground mb-8 max-w-2xl">
            A quick glance before you submit. After submitting, these answers are locked — your Week 1 self needs to stay your Week 1 self.
          </p>
          <div className="border border-border bg-card p-6 mb-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Statements rated</div>
            <div className="grid grid-cols-10 gap-1.5">
              {answers.map((v, i) => (
                <div key={i} data-testid={`assessment-review-pip-${i + 1}`}
                  className={`aspect-square flex items-center justify-center text-xs font-serif border ${v ? "border-primary bg-primary/10 text-primary" : "border-destructive/30 bg-destructive/5 text-destructive"}`}>
                  {v || "—"}
                </div>
              ))}
            </div>
            {!allRated && (
              <p className="text-xs text-destructive mt-4">Some statements still need a rating. Tap "Back" to finish them.</p>
            )}
          </div>
          <div className="border border-border bg-card p-6 mb-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Reflections</div>
            <ul className="space-y-4">
              {reflectionPrompts.map((p) => (
                <li key={p.key}>
                  <div className="text-sm text-muted-foreground mb-1">{p.prompt}</div>
                  <div className="font-serif italic leading-relaxed">
                    {(reflections[p.key] || "").trim() || <span className="not-italic text-destructive text-sm">Not yet written</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Lock className="w-3.5 h-3.5" />
            <span>Once submitted, answers can't be edited. That's by design — your "then" needs to stay honest.</span>
          </div>
        </section>
      )}

      {/* Nav */}
      <div className="mt-16 flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          disabled={screen === 0}
          onClick={() => setScreen((s) => Math.max(0, s - 1))}
          data-testid="assessment-back"
          className="rounded-full">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        {screen < totalScreens - 1 ? (
          <Button
            type="button"
            onClick={() => setScreen((s) => Math.min(totalScreens - 1, s + 1))}
            disabled={!canNext}
            data-testid="assessment-next"
            className="rounded-full">
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            type="button"
            onClick={submit}
            disabled={submitting || !allRated || !allReflected}
            data-testid="assessment-submit"
            className="rounded-full">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : "Submit & lock"}
          </Button>
        )}
      </div>
    </div>
  );
}
